import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { LogOut, RefreshCw, BarChart3, Clock, Mail, FileText, Settings, MessageSquare, ClipboardList } from 'lucide-react'
import { toast } from 'sonner'

export default function AdminDashboard() {
  const { admin, token, logout } = useAuth()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [token])

  const loadStats = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      await api.getAdminStats(token)
    } catch (error) {
      toast.error('Erro ao carregar estatísticas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    toast.success('Logout realizado')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif font-bold text-gray-900">Painel Administrativo</h1>
            <p className="text-sm text-gray-500">Bem-vindo, {admin?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </header>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <a
            href="/admin/submissions/contact"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                <Mail size={20} className="text-purple-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Fale Conosco</h3>
            </div>
            <p className="text-sm text-gray-500">Visualize todas as mensagens do formulário de contato</p>
            <p className="text-xs text-purple-600 mt-2 font-medium">Ver mensagens →</p>
          </a>

          <a
            href="/admin/stages"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#51A64D]/10 rounded-lg flex items-center justify-center group-hover:bg-[#51A64D] transition-colors">
                <BarChart3 size={20} className="text-[#51A64D] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Gerenciar Fases</h3>
            </div>
            <p className="text-sm text-gray-500">Altere a fase atual do projeto e acompanhe o progresso</p>
            <p className="text-xs text-[#51A64D] mt-2 font-medium">Gerenciar →</p>
          </a>

          <a
            href="/admin/documents"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center group-hover:bg-indigo-500 transition-colors">
                <FileText size={20} className="text-indigo-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Documentos</h3>
            </div>
            <p className="text-sm text-gray-500">Gerencie os documentos públicos do projeto</p>
            <p className="text-xs text-indigo-600 mt-2 font-medium">Gerenciar →</p>
          </a>

          <a
            href="/admin/reports"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#2C5B8A]/10 rounded-lg flex items-center justify-center group-hover:bg-[#2C5B8A] transition-colors">
                <ClipboardList size={20} className="text-[#2C5B8A] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Relatórios</h3>
            </div>
            <p className="text-sm text-gray-500">Publique o relatório da Escuta Social</p>
            <p className="text-xs text-[#2C5B8A] mt-2 font-medium">Gerenciar →</p>
          </a>

          <a
            href="/admin/faq"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                <MessageSquare size={20} className="text-amber-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Perguntas Frequentes</h3>
            </div>
            <p className="text-sm text-gray-500">Adicione, edite ou remova perguntas frequentes</p>
            <p className="text-xs text-amber-600 mt-2 font-medium">Gerenciar →</p>
          </a>

          <a
            href="/admin/settings"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                <Settings size={20} className="text-amber-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Configurações</h3>
            </div>
            <p className="text-sm text-gray-500">Edite os textos do site e informações de contato</p>
            <p className="text-xs text-amber-600 mt-2 font-medium">Gerenciar →</p>
          </a>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <a
            href="/admin/logs"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-[#51A64D] transition-colors"
          >
            <Clock size={16} />
            Ver Logs do Sistema
          </a>
          
          <button
            onClick={loadStats}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-[#51A64D] transition-colors"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>
    </div>
  )
}